import { useEffect, useState } from "react";
import { useParams } from "react-router";

import type { AxiosError } from "axios";
import type { Socket } from "socket.io-client";

import { useChatbotControllerGetChatbotV1 } from "@virtual-brain-lib/frontend-api";
import {
	type ChatOptions,
	ChatProvider,
	useChatbotSessionStorage,
} from "@virtual-brain-lib/frontend-design-system/components";
import { Spinner } from "@virtual-brain-lib/frontend-design-system/ui";
import { useSearchParams } from "@virtual-brain-lib/frontend-utils";

import { authenticate } from "../../utils/auth";
import { initSocket } from "../../utils/socket";
import { getAccessToken } from "../../utils/storage";
import NotFound from "../errors/NotFound";
import Unauthorized from "../errors/Unauthorized";
import ChatbotAppMessages from "./ChatbotAppChatMessages";

export default function ChatbotAppChat() {
	const { id: chatbotId = "" } = useParams();
	const [socket, setSocket] = useState<Socket>();
	const [searchParams] = useSearchParams();
	const isPreview = Boolean(searchParams.get("preview"));
	const [isAuthenticated, setIsAuthenticated] = useState(false);
	const [authError, setAuthError] = useState<number>();

	const { sessionId } = useChatbotSessionStorage({ isPreview, chatbotId });

	const existingToken = getAccessToken(chatbotId, isPreview);

	useEffect(() => {
		const handleAuth = async () => {
			if (!chatbotId) return;

			if (existingToken) {
				setIsAuthenticated(true);
			}

			try {
				await authenticate(chatbotId);
				setIsAuthenticated(true);
			} catch (err) {
				setAuthError((err as AxiosError)?.response?.status);
			}
		};

		handleAuth();
	}, [chatbotId]);

	const { data: chatbot, error } = useChatbotControllerGetChatbotV1(chatbotId, {
		query: {
			enabled: Boolean(chatbotId) && isAuthenticated,
		},
	});

	useEffect(() => {
		if (!chatbotId || !sessionId) return;

		const accessToken = getAccessToken(chatbotId, isPreview) ?? "";
		const newSocket = initSocket({ chatbotId, accessToken, sessionId });

		newSocket.connect();
		setSocket(newSocket);

		return () => {
			newSocket.disconnect();
		};
	}, [chatbotId, sessionId]);

	if (authError === 401 || error?.response?.status === 401) {
		return <Unauthorized />;
	}

	if (authError || error) {
		return <NotFound />;
	}

	if (!chatbot) {
		return (
			<div className="flex h-full w-full items-center justify-center bg-white">
				<Spinner />
			</div>
		);
	}

	const chatOptions: ChatOptions = {
		type: "chatbot",
		analyticsEventDomain: "Chatbot",
		displaySources: chatbot.displaySources,
		displaySuggestedPrompts: chatbot.displaySuggestedPrompts,
		displayActions: false,
		displayTips: false,
		botIcon:
			chatbot.profilePicture?.location ?? "https://files.getvirtualbrain.com/logo_min.png",
		color: chatbot.color,
	};

	return (
		<div className="relative flex h-full max-w-full flex-1 flex-col overflow-hidden bg-white">
			<div className="relative top-0 flex h-12 w-full items-center justify-center">
				<h2 className="text-xl leading-6 font-bold text-default">{chatbot.name}</h2>
			</div>
			<ChatProvider options={chatOptions} socket={socket}>
				<ChatbotAppMessages chatbot={chatbot} chatbotSessionId={sessionId} />
			</ChatProvider>
		</div>
	);
}
